import { useRef, useState } from 'react'
import { FileCheck2, Upload } from 'lucide-react'
import { kit } from './strings'
import './FileField.css'


/**
 * File picker with drag-and-drop. Wraps a native file input so the trigger and the
 * "no file" hint show in Arabic rather than the browser's own locale.
 *
 * NO PROGRESS BAR. Choosing a file used to start one — a determinate bar with a
 * percentage, simulated by a timer when nothing was actually being sent, and driven by
 * `XHR.upload.onprogress` when something was. Both were misleading in the same way:
 * nothing is transferred when a file is PICKED (the form posts on save), so the bar
 * announced work that had not started and finished before it did.
 *
 * What the control says now is what it knows: which file is chosen. The save button
 * carries the waiting, which is where the waiting actually is.
 */
export default function FileField({
  id,
  name,
  accept,
  required,
  onChange,
  buttonLabel = kit.chooseFile,
  emptyLabel = kit.noFile,
}) {
  const inputRef = useRef(null)
  const [fileName, setFileName] = useState('')
  const [dragging, setDragging] = useState(false)

  function select(file) {
    setFileName(file?.name ?? '')
    onChange?.(file)
  }

  /** Mirrors the browser's own `accept` filtering for dropped files. */
  function isAccepted(file) {
    if (!accept) return true
    return accept
      .split(',')
      .map((part) => part.trim().toLowerCase())
      .some((part) =>
        part.startsWith('.')
          ? file.name.toLowerCase().endsWith(part)
          : file.type === part || part === '*/*',
      )
  }

  function handleDrop(e) {
    e.preventDefault()
    setDragging(false)

    const file = e.dataTransfer.files?.[0]
    if (!file || !isAccepted(file)) return

    // Keep the native input in sync so the control stays form-associated.
    const transfer = new DataTransfer()
    transfer.items.add(file)
    if (inputRef.current) inputRef.current.files = transfer.files

    select(file)
  }

  const selected = Boolean(fileName)

  return (
    <label
      className={[
        'file-field',
        selected && 'file-field--selected',
        dragging && 'file-field--dragging',
      ]
        .filter(Boolean)
        .join(' ')}
      htmlFor={id}
      onDragOver={(e) => {
        e.preventDefault()
        setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
    >
      <input
        ref={inputRef}
        id={id}
        name={name}
        type="file"
        accept={accept}
        required={required}
        className="file-field__input"
        onChange={(e) => select(e.target.files?.[0] ?? null)}
        data-testid="file-field"
      />

      <span className="file-field__icon" aria-hidden="true">
        {selected ? <FileCheck2 size={18} /> : <Upload size={18} />}
      </span>

      <span className="file-field__body">
        <span className={`file-field__name${selected ? '' : ' file-field__name--empty'}`}>
          <bdi>{fileName || emptyLabel}</bdi>
        </span>

      </span>

      <span className="file-field__action">{buttonLabel}</span>
    </label>
  )
}
